import { z } from "zod";
import { Label } from "components/label";
import { Controller, useForm } from "react-hook-form";
import { HttpError, useOne, useUpdate } from "@refinedev/core";
import { zodResolver } from "@hookform/resolvers/zod";
import { SwitchButton } from "components/switchButton";
import Input from "components/input";
import Button from "components/button";
import { Link, useNavigate } from "react-router-dom";
import { FullScreenLoading } from "components/fullScreenLoading";
import toast from "react-hot-toast";
import { ISupportCategoryIds, ISupportQADetail, ISupportQAForm } from "interfaces";
import Select from "components/select";
import { get } from "lodash";
import { TextEditor } from "components/textEditor";
import { REGEX_DOMAIN } from "utills/constants";

interface EditSupportQAProps {
  id: string;
  defaultData: ISupportQADetail;
}
const EditSupportQASchema = z.object({
  is_active: z.boolean(),
  slug: z
    .string()
    .min(1, "This field is required")
    .refine((value) => REGEX_DOMAIN.test(value ?? ""), "Invalid slug"),
  support_center_category_id: z.string().min(1, "This field is required"),
  title_en: z.string().min(1, "This field is required"),
  title_vi: z.string().min(1, "This field is required"),
  detail_en: z.string().min(1, "This field is required"),
  detail_vi: z.string().min(1, "This field is required"),
});

export const EditSupportQAForm = ({id, defaultData}: EditSupportQAProps) => {

  const { data, isLoading: isLoadingCategories } = useOne<ISupportCategoryIds[], HttpError>({
    resource: "cs/support-center/categories/dropdown",
    id: "",
    meta: {
      resource: "data"
    }
  });

  const categoryIds = (data?.data ?? []).map((category: ISupportCategoryIds) => (
    { value: category.id, label: category.name_vi }
  ));

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ISupportQAForm, HttpError, ISupportQAForm>({
    defaultValues: {
      title_en: defaultData.title_en ?? "",
      title_vi: defaultData.title_vi ?? "",
      detail_en: defaultData.detail_en ?? "",
      detail_vi: defaultData.detail_vi ?? "",
      slug: defaultData.slug ?? "",
      support_center_category_id: defaultData.support_center_category_id ?? "",
      is_active: defaultData.is_active ?? true,
    },
    resolver: zodResolver(EditSupportQASchema),
  });

  const { mutate, isLoading: isLoading } = useUpdate();
  const navigate = useNavigate();

  const onSubmit = (values: ISupportQAForm) => {
    const submitValues = { ...values };

    mutate(
      {
        resource: "cs/support-center/qas",
        id: id,
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...submitValues,
        },
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
          navigate(`/cs/support-qa/${id}`);
        },
      }
    );
  };

  return (
    <div className="w-full ">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-y-2 sm:gap-y-4">
          <div className="w-full box-container">
            <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6">
              <div className="flex flex-col gap-4 sm:gap-6 sm:flex-row sm:col-span-3">
                <div className="shrink-0">
                  <Label required id="is_active" name="Status" />
                  <Controller
                    control={control}
                    name="is_active"
                    defaultValue={true}
                    render={({ field: { onChange, value = true, ref } }) => (
                      <div className="sm:mt-4">
                        <SwitchButton checked={value} onChange={onChange} />
                      </div>
                    )}
                  />
                </div>
                <div className="flex-1">
                  <Label required id="slug" name="Slug" />
                  <Input
                    id="slug"
                    placeholder="Slug"
                    error={errors["slug"]}
                    {...register("slug")}
                  />
                  {errors["slug"] ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {errors?.slug.message}
                      </span>
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="sm:col-span-3">
                <Label required id="support_center_category_id" name="Category" />
                <Controller
                  control={control}
                  name={"support_center_category_id"}
                  render={({ field: { onChange, value = "", ref } }) => {
                    return (
                      <Select
                        key="support_center_category_id"
                        inputRef={ref}
                        options={categoryIds}
                        isLoading={isLoadingCategories}
                        id={`support_center_category_id`}
                        placeholder="Category"
                        defaultValue={
                          categoryIds.find(
                            (selected: { label: string; value: any }) =>
                              selected.value === value
                          ) ?? ""
                        }
                        value={categoryIds.find(
                          (selected: { label: string; value: any }) =>
                            selected.value === value
                        )}
                        onChange={(selected: { label: string; value: any }) => {
                          onChange(selected?.value);
                        }}
                        error={get(errors, `support_center_category_id`, "")}
                      />
                    );
                  }}
                />
                {get(errors, `support_center_category_id`, "") ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(errors, `support_center_category_id.message`, "")}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-3">
                <Label required id="title_vi" name="Q&A title vie" />
                <Input
                  id="title_vi"
                  placeholder="Q&A title vie"
                  error={errors["title_vi"]}
                  {...register("title_vi")}
                />
                {errors["title_vi"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.title_vi.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-3">
                <Label required id="title_en" name="Q&A title eng" />
                <Input
                  id="title_en"
                  placeholder="Q&A title eng"
                  error={errors["title_en"]}
                  {...register("title_en")}
                />
                {errors["title_en"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.title_en.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-3">
                <Label 
                  required
                  id="detail_vi" 
                  name="Content vie"
                />
                <Controller
                  control={control}
                  name="detail_vi"
                  render={({ field: { onChange, value = "" } }) => (
                    <TextEditor
                      value={value}
                      onChange={onChange}
                    />
                  )}
                />
                {errors["detail_vi"] ? ( 
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.detail_vi.message}
                    </span>
                  </p> 
                ): null}
              </div>
              <div className="sm:col-span-3">
                <Label 
                  required
                  id="detail_en" 
                  name="Content eng"
                />
                <Controller
                  control={control}
                  name="detail_en"
                  render={({ field: { onChange, value = "" } }) => (
                    <TextEditor
                      value={value}
                      onChange={onChange}
                    />
                  )}
                />
                {errors["detail_en"] ? ( 
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.detail_en.message}
                    </span>
                  </p> 
                ): null}
              </div>
            </div>
          </div>
          <div className="w-full px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2] mt-auto">
            <div className="flex items-center justify-end gap-x-4">
              <Link to={`/cs/support-qa/${id}`}>
                <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                  Cancel
                </Button>
              </Link>
              <Button className="w-20" type="submit">
                Save
              </Button>
            </div>
          </div>
        </div>
        {isLoading ? <FullScreenLoading /> : <></>}
      </form>
    </div>
  );
};